// The claim part of a claim's item page: set-up, money, Claude read, document review, lines, variations, evidence,
// approval & signing. Everything else on the page (stages, onus, requests & revisions, comments, history) is shared.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/app';
import { write } from '../../lib/api';
import { money } from '../../lib/claimCalc';
import type { ClaimLine } from '../../lib/claimData';
import type { Item } from '../../lib/registers';
import { fmtDate } from '../../lib/format';
import type { FileRow } from '../ItemFiles';
import { Button, Card, errText, useToast } from '../kit';
import { Facts, type FactSpec } from '../item/Facts';
import { useClaim } from './useClaim';
import { MoneyStrip, Assessment } from './Money';
import { Lines } from './Lines';
import { Variations } from './Variations';
import { ClaudeRead } from './ClaudeRead';
import { Evidence } from './Evidence';
import { Approval } from './Approval';
import { Review } from './Review';

type ContractOpt = { id: string; title: string | null; contract_no: string | null; building_id: string | null; building_ids: string[] | null; sum_vep: number | null };

function Setup({ item, onDone }: { item: Item; onDone: () => void }) {
  const toast = useToast();
  const [contracts, setContracts] = useState<ContractOpt[]>([]);
  const [con, setCon] = useState('');
  const [no, setNo] = useState(/(\d+)/.exec(item.ref_no ?? '')?.[1] ?? /claim\s*#?\s*(\d+)/i.exec(item.title)?.[1] ?? '');
  const [period, setPeriod] = useState('');
  useEffect(() => {
    void supabase
      .from('contracts')
      .select('id, title, contract_no, building_id, building_ids, sum_vep')
      .eq('site_id', item.site_id)
      .eq('has_claims', true)
      .is('deleted_at', null)
      .then(({ data }) => {
        // contracts on this claim's building first (contracts live on buildings since V0.4.2)
        const on = (x: ContractOpt) => (x.building_ids ?? (x.building_id ? [x.building_id] : [])).some((b) => item.building_ids.includes(b));
        const list = ((data ?? []) as ContractOpt[]).sort((a, b) => Number(on(b)) - Number(on(a)));
        setContracts(list);
        setCon((c) => c || list.find(on)?.id || '');
      });
  }, [item.site_id, item.building_ids]);
  async function save() {
    try {
      await write('claim_details', 'insert', {
        values: { item_id: item.id, contract_id: con || null, claim_no: no ? Number(no) : null, period_to: period || null, pbs_total_vip: item.amount ?? null },
        summary: 'Set up the claim (contract, claim no.)',
      });
      onDone();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  return (
    <Card title="Set up this claim">
      <p className="small" style={{ marginTop: 0 }}>
        Link the claim to its building contract — the deposit, retention and running totals come from the contract. {contracts.length === 0 ? 'No contracts with claims on this site yet: add one on the building page (Buildings & projects).' : ''}
      </p>
      <div className="fu-form">
        <select value={con} onChange={(e) => setCon(e.target.value)} aria-label="Contract">
          <option value="">Contract…</option>
          {contracts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title ?? c.contract_no} {c.contract_no ? `· ${c.contract_no}` : ''}
            </option>
          ))}
        </select>
        <input inputMode="numeric" value={no} onChange={(e) => setNo(e.target.value.replace(/\D/g, ''))} placeholder="Claim no." aria-label="Claim number" />
        <input type="date" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period to" />
        <Button kind="primary" onClick={() => void save()} disabled={!con}>
          Set up
        </Button>
      </div>
    </Card>
  );
}

export function ClaimSection({ item, files, canEdit, canComment, onFiles }: { item: Item; files: FileRow[]; canEdit: boolean; canComment: boolean; onFiles: () => void }) {
  const { access } = useApp();
  const toast = useToast();
  const { data, error, reload } = useClaim(item.id);
  const [prev, setPrev] = useState<{ label: string; lines: ClaimLine[] } | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const internal = Boolean(access?.super_admin || access?.roles.some((r) => ['pm', 'field', 'swi_finance'].includes(r.role)));
  const det = data?.details ?? null;
  const locked = Boolean(det?.locked_at);
  const isSuper = Boolean(access?.super_admin);
  const editable = canEdit && (!locked || isSuper);

  // the previous claim on the same contract — to carry its lines forward
  useEffect(() => {
    if (!det?.contract_id || !det.claim_no) return setPrev(null);
    void (async () => {
      const { data: p } = await supabase.from('claim_details').select('item_id, claim_no').eq('contract_id', det.contract_id!).lt('claim_no', det.claim_no!).is('deleted_at', null).order('claim_no', { ascending: false }).limit(1);
      const row = (p ?? [])[0] as { item_id: string; claim_no: number } | undefined;
      if (!row) return setPrev(null);
      const { data: ls } = await supabase.from('claim_lines').select('*').eq('item_id', row.item_id).is('deleted_at', null).order('sort');
      setPrev({ label: `claim ${row.claim_no}`, lines: (ls ?? []) as ClaimLine[] });
    })();
  }, [det?.contract_id, det?.claim_no]);

  const reload2 = () => {
    void reload();
    onFiles();
  };
  const facts: FactSpec[] = useMemo(() => {
    if (!det) return [];
    const m = data!.model;
    const f: FactSpec[] = [
      { key: 'claim_no', label: 'Claim no.', type: 'number', value: det.claim_no, display: det.claim_no ?? '—' },
      { key: 'period_to', label: 'Period to', type: 'date', value: det.period_to, display: fmtDate(det.period_to) },
      { key: 'pbs_total_vip', label: 'PBS claim total (VIP, as on their document)', type: 'number', value: det.pbs_total_vip, display: det.pbs_total_vip === null ? '—' : `FJD ${money(det.pbs_total_vip)}` },
      { key: 'contract', label: 'Contract', type: 'readonly', value: null, display: data!.contract ? `${data!.contract.title ?? ''} · ${data!.contract.contract_no ?? ''} · FJD ${money(m.terms.sum_vep)} VEP` : 'Not linked' },
    ];
    if (!m.lines.length) {
      f.push({ key: 'works_claimed', label: 'Works claimed this period (VEP)', type: 'number', value: det.works_claimed, display: det.works_claimed === null ? '—' : money(det.works_claimed) });
      f.push({ key: 'works_swi', label: 'Works SWI agrees (VEP)', type: 'number', value: det.works_swi, display: det.works_swi === null ? 'same as claimed' : money(det.works_swi) });
    }
    f.push({ key: 'deposit_override', label: 'Deposit recovered — type to override', type: 'number', value: det.deposit_override, display: det.deposit_override === null ? `${money(m.swi.deposit.thisClaim)} (worked out)` : money(det.deposit_override) });
    f.push({ key: 'retention_override', label: 'Retention — type to override', type: 'number', value: det.retention_override, display: det.retention_override === null ? `${money(m.swi.retention.thisClaim)} (worked out)` : money(det.retention_override) });
    return f;
  }, [det, data]);

  if (error) return <div className="error-box">{error}</div>;
  if (!data) return <div className="empty">Loading the claim…</div>;
  if (!det) return canEdit ? <Setup item={item} onDone={() => void reload()} /> : <div className="empty">This claim hasn’t been set up yet.</div>;
  const m = data.model;
  if (!data.contract) return <div className="banner banner-warn">This claim isn’t linked to a contract — add the contract on the building page (Buildings & projects) and link it here.</div>;
  const missing = [!m.terms.sum_vep && 'contract sum', !m.terms.deposit_recovery_pct && m.terms.deposit_pct && 'deposit recovered per claim %', !m.terms.retention_limit_pct && 'retention limit %'].filter(Boolean);

  return (
    <div className="claim-sec">
      <MoneyStrip m={m} pbsTotal={det.pbs_total_vip} certified={locked} />
      {missing.length ? <div className="banner banner-warn">The contract is missing: {missing.join(', ')} — fill it in on the building page (Buildings & projects).</div> : null}
      {locked ? <div className="banner banner-info">Certified and locked {fmtDate(det.certified_at)} · {det.cert_ref}. {isSuper ? 'As Super Admin you can still amend — every change is logged.' : 'Changes now go through the Super Admin.'}</div> : null}

      <Card title="Claim details">
        <Facts
          facts={facts}
          canEdit={editable}
          onSave={async (key, value, label) => {
            try {
              await write('claim_details', 'update', { id: det.id, values: { [key]: value, _expect_updated_at: det.updated_at }, summary: `Changed ${label}` });
              await reload();
              return true;
            } catch (e) {
              toast({ tone: 'error', text: errText(e) });
              return false;
            }
          }}
        />
      </Card>

      {internal && editable ? <ClaudeRead itemId={item.id} files={files} details={det} lines={m.lines} varCount={m.vars.length} onChanged={reload2} /> : null}

      <section className="card">
        <button type="button" className={`histh ${reviewOpen ? 'open' : ''}`} aria-expanded={reviewOpen} onClick={() => setReviewOpen(!reviewOpen)}>
          <svg className="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
          <span className="k">Document review</span>
          <span className="muted small">Comment on the claim PDF itself — SWI, the architect and client finance; then send to PBS</span>
        </button>
        {reviewOpen ? (
          <div className="card-body">
            <Review itemId={item.id} files={files} canComment={canComment} canSend={internal && canEdit} internal={internal} />
          </div>
        ) : null}
      </section>

      <Card title="Money summary — claim assessment">
        <Assessment m={m} />
      </Card>

      <Card title={`Claim lines vs BOQ${m.lines.length ? ` · ${m.lines.length}` : ''}`}>
        <Lines itemId={item.id} lines={m.lines} canEdit={editable} previousLines={prev} onChanged={() => void reload()} />
      </Card>

      <Card title="Variations in this claim">
        <Variations
          itemId={item.id}
          checks={m.vars}
          reg={data.regVars}
          approvedTotal={m.approvedVariations}
          before={m.variationsBefore}
          thisSwi={m.varSwi}
          claimed={m.varClaimed}
          canEdit={editable}
          onChanged={() => void reload()}
        />
      </Card>

      <div className="two">
        <Card title="Progress evidence">
          <Evidence itemId={item.id} siteId={item.site_id} files={files} canEdit={canEdit} onChanged={onFiles} />
        </Card>
        <div id="claim-approval">
        <Card title="Approval & signing">
          <Approval itemId={item.id} details={det} model={m} signatures={data.signatures} files={files} architectOrgId={data.contract.architect_org_id} onChanged={reload2} />
        </Card>
        </div>
      </div>
    </div>
  );
}
